// IndexedDB Offline Storage for MagicMail
const DB_NAME = "magicmail_offline_db";
const DB_VERSION = 1;

// La caché se aísla por cuenta: cada buzón tiene su propia base de datos para
// evitar que correos de una cuenta se filtren a otra al cambiar de cuenta.
let activeAccountKey = "default";

function hashAccount(email: string): string {
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < email.length; i++) {
    const ch = email.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (h2 >>> 0).toString(16).padStart(8, "0") + (h1 >>> 0).toString(16).padStart(8, "0");
}

export function setActiveOfflineAccount(email: string | null | undefined): void {
  activeAccountKey = email ? hashAccount(email.trim().toLowerCase()) : "default";
}

function getDBName(): string {
  return `${DB_NAME}_${activeAccountKey}`;
}

export interface OutboxItem {
  id: string; // generated uuid/timestamp
  from?: string;
  to: any[];
  cc?: any[];
  bcc?: any[];
  subject: string;
  body?: string;
  bodyHtml?: string;
  clientMessageId?: string;
  attachments?: Array<{
    name: string;
    type: string;
    size: number;
    base64: string;
  }>;
  createdAt: number;
  status: "pending" | "sending" | "failed";
  retryCount: number;
  errorMessage?: string;
}

export interface CachedEmail {
  id: string; // folder:uid
  uid: string;
  folderId: string;
  from: any;
  to: any[];
  cc?: any[];
  bcc?: any[];
  subject: string;
  snippet?: string;
  body?: string;
  bodyHtml?: string;
  date: string;
  isRead: boolean;
  isFlagged?: boolean;
  labels?: string[];
  attachments?: any[];
  headers?: any;
  cachedAt: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      return reject(new Error("IndexedDB not supported"));
    }

    const request = indexedDB.open(getDBName(), DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Store: emails
      if (!db.objectStoreNames.contains("emails")) {
        const emailStore = db.createObjectStore("emails", { keyPath: "id" });
        emailStore.createIndex("folderId", "folderId", { unique: false });
        emailStore.createIndex("date", "date", { unique: false });
        emailStore.createIndex("cachedAt", "cachedAt", { unique: false });
      }

      // Store: folders
      if (!db.objectStoreNames.contains("folders")) {
        db.createObjectStore("folders", { keyPath: "id" });
      }

      // Store: outbox (pending outgoing messages)
      if (!db.objectStoreNames.contains("outbox")) {
        const outboxStore = db.createObjectStore("outbox", { keyPath: "id" });
        outboxStore.createIndex("status", "status", { unique: false });
        outboxStore.createIndex("createdAt", "createdAt", { unique: false });
      }

      // Store: meta
      if (!db.objectStoreNames.contains("meta")) {
        db.createObjectStore("meta", { keyPath: "key" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ── Email Caching ──

export async function saveEmailsToCache(folderId: string, emails: any[]): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction("emails", "readwrite");
    const store = tx.objectStore("emails");

    const now = Date.now();
    for (const email of emails) {
      const id = `${folderId}:${email.id || email.uid}`;
      const item: CachedEmail = {
        id,
        uid: String(email.id || email.uid),
        folderId,
        from: email.from,
        to: email.to || [],
        cc: email.cc,
        bcc: email.bcc,
        subject: email.subject || "(Sin asunto)",
        snippet: email.snippet,
        body: email.body,
        bodyHtml: email.bodyHtml,
        date: email.date || new Date().toISOString(),
        isRead: !!email.isRead,
        isFlagged: !!email.isFlagged,
        labels: email.labels || [],
        attachments: email.attachments,
        headers: email.headers,
        cachedAt: now,
      };
      store.put(item);
    }

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn("IndexedDB: Failed to cache emails", err);
  }
}

export async function saveSingleEmailToCache(folderId: string, email: any): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction("emails", "readwrite");
    const store = tx.objectStore("emails");

    const id = `${folderId}:${email.id || email.uid}`;
    const now = Date.now();

    const existingReq = store.get(id);
    existingReq.onsuccess = () => {
      const existing = existingReq.result || {};
      const item: CachedEmail = {
        ...existing,
        id,
        uid: String(email.id || email.uid),
        folderId,
        from: email.from || existing.from,
        to: email.to || existing.to || [],
        cc: email.cc || existing.cc,
        bcc: email.bcc || existing.bcc,
        subject: email.subject ?? existing.subject ?? "(Sin asunto)",
        snippet: email.snippet ?? existing.snippet,
        body: email.body ?? existing.body,
        bodyHtml: email.bodyHtml ?? existing.bodyHtml,
        date: email.date || existing.date || new Date().toISOString(),
        isRead: email.isRead !== undefined ? !!email.isRead : existing.isRead,
        isFlagged: email.isFlagged !== undefined ? !!email.isFlagged : existing.isFlagged,
        labels: email.labels || existing.labels || [],
        attachments: email.attachments || existing.attachments,
        headers: email.headers || existing.headers,
        cachedAt: now,
      };
      store.put(item);
    };

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn("IndexedDB: Failed to cache single email", err);
  }
}

export async function getCachedEmailsByFolder(folderId: string): Promise<CachedEmail[]> {
  try {
    const db = await openDB();
    const tx = db.transaction("emails", "readonly");
    const store = tx.objectStore("emails");
    const index = store.index("folderId");
    const request = index.getAll(folderId);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const results: CachedEmail[] = request.result || [];
        results.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.warn("IndexedDB: Failed to get cached emails for folder", folderId, err);
    return [];
  }
}

export async function getCachedEmailById(folderId: string, emailId: string): Promise<CachedEmail | null> {
  try {
    const db = await openDB();
    const tx = db.transaction("emails", "readonly");
    const store = tx.objectStore("emails");
    const id = `${folderId}:${emailId}`;
    const request = store.get(id);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.warn("IndexedDB: Failed to get cached email", `${folderId}:${emailId}`, err);
    return null;
  }
}

// ── Folders Caching ──

export async function saveFoldersToCache(folders: any[]): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction("folders", "readwrite");
    const store = tx.objectStore("folders");
    store.clear();
    for (const folder of folders) {
      store.put(folder);
    }
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn("IndexedDB: Failed to cache folders", err);
  }
}

export async function getCachedFolders(): Promise<any[]> {
  try {
    const db = await openDB();
    const tx = db.transaction("folders", "readonly");
    const store = tx.objectStore("folders");
    const request = store.getAll();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.warn("IndexedDB: Failed to get cached folders", err);
    return [];
  }
}

export function generateClientMessageId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `cli_${crypto.randomUUID()}`;
  }
  return `cli_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 12)}`;
}

// ── Outbox Queue ──

export async function addToOutbox(item: Omit<OutboxItem, "id" | "createdAt" | "status" | "retryCount">): Promise<OutboxItem> {
  const db = await openDB();
  const tx = db.transaction("outbox", "readwrite");
  const store = tx.objectStore("outbox");

  const outboxItem: OutboxItem = {
    ...item,
    id: `outbox_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    createdAt: Date.now(),
    status: "pending",
    retryCount: 0,
  };

  store.put(outboxItem);

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(outboxItem);
    tx.onerror = () => reject(tx.error);
  });
}

export async function getOutboxItems(): Promise<OutboxItem[]> {
  try {
    const db = await openDB();
    const tx = db.transaction("outbox", "readonly");
    const store = tx.objectStore("outbox");
    const request = store.getAll();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const items: OutboxItem[] = request.result || [];
        items.sort((a, b) => a.createdAt - b.createdAt);
        resolve(items);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.warn("IndexedDB: Failed to get outbox items", err);
    return [];
  }
}

export async function removeOutboxItem(id: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction("outbox", "readwrite");
    const store = tx.objectStore("outbox");
    store.delete(id);

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn("IndexedDB: Failed to remove outbox item", id, err);
  }
}

export async function updateOutboxItemStatus(
  id: string,
  status: "pending" | "sending" | "failed",
  errorMessage?: string
): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction("outbox", "readwrite");
    const store = tx.objectStore("outbox");
    const req = store.get(id);

    req.onsuccess = () => {
      const item: OutboxItem = req.result;
      if (item) {
        item.status = status;
        item.retryCount = (item.retryCount || 0) + 1;
        if (errorMessage) item.errorMessage = errorMessage;
        store.put(item);
      }
    };

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn("IndexedDB: Failed to update outbox status", id, err);
  }
}
